import { randId, } from 'utils/string';
import utilsCookies from 'utils/cookies';
import { BaseStore, } from 'stores/baseStore';
import { registerInNamespace, } from 'application/nm';
import { namespacedConsts, } from 'utils/object';
import userActions from 'actions/userToolbarActions';
import analyticsActions from 'actions/analyticsActions';
import safeLocalStorage from 'providers/safeLocalStorageProvider';
import { get as getFromPdo, DEFAULT_LOCALE, } from 'application/pageDataObject';
import { facebookAuthenticationProvider, } from 'providers/facebook/facebookAuthenticationProvider';


const CLASS_CONSTATNTS = namespacedConsts({
  USER: {
    START_DATE: true,
    SESSION_START_TIME: true,
  },
});

const GUEST_ID_KEY = 'GusetUserId';  // for backwards compatibility
const STORAGE_KEY = 'CurrentUser';   // for backwards compatibility
const LOGGED_IN_COOKIE_NAME = 'logged_in';
const USER_AUTHENTICATION_TTL = 24 * 60 * 60 * 1000;  //1 day
export const MAX_SESSION_DURATION = 30 * 60 * 1000;  //30 minutes
const LOGOUT_PATH = getFromPdo('config.logout_path');

class UserStore extends BaseStore {

  constructor(options = {}) {
    super(options);
    this.loadFromStorage();
    this.recentReads = this.recentReads || {};
    if (this.isNewSession) { this.resetSessionStartTime(); }
    if (this.isLoggedIn && this.isLoginExpired()) {
      this.isAuthenticated = false;
      this.authenticationProvider.reAuthenticate();
    }
  }

  routeAction(action){
    let { type, } = action;
    let ACTIONS = userActions.TYPES;
    switch (type) {
      case ACTIONS.FB_SDK_READY:
        return (this.state.fbSdkReady = true);
      case ACTIONS.FB_LOGIN:
        return this.fbLoggingIn();
      case ACTIONS.EXISTING_USER_AUTHENTICATED:
        return this.existingUserAuthenticated(action.userData);
      case ACTIONS.NEW_USER_AUTHENTICATED:
        return this.newUserAuthenticated(action.userData);
      case ACTIONS.LOGOUT:
        return this.logoutUser();
      case ACTIONS.AUTHENTICATION_FAILED:
        return this.authenticationFailed(action.response);
      case ACTIONS.UNIMPERSONATE:
        return this.assignData(action.response.data);
      case analyticsActions.TYPES.PAGE_VIEWED:
        return this.updateRecentReads();
      default:
        return this;
    }
  }

  get authenticationProvider(){
    // At the moment we use only facebook, so...
    return facebookAuthenticationProvider;
  }

  get isAdmin(){ return !!this.state.admin; }
  get isAuthor(){ return this.isAuthenticated && this.state.id === getFromPdo('post.author.id'); }

  get isLoggedIn(){ return !!this.state.loggedIn; }

  get isImpersonating(){ return !!this.state.impersonatingId; }

  get isAuthenticated(){ return this.state.authenticated; }
  set isAuthenticated(bool){ this.state.authenticated = bool; }

  get isAuthenticating(){ return this.authenticating; }
  set isAuthenticating(bool){ this.authenticating = bool; }

  get authenticatedAt(){ return this.state.authenticatedAt; }
  set authenticatedAt(time){ this.state.authenticatedAt = time; }

  get recentReads(){ return this.state.recentReads; }
  set recentReads(readsObj){ this.state.recentReads = readsObj; }

  get team(){ return this.state.team || ''; }

  updateRecentReads(){
    this.deleteOldReads();
    this.setCurrentPostRead();
    this.emitChange();
  }

  deleteOldReads(){
    for (let postId in this.recentReads) {
      if (this.recentReads[postId].time < Date.now() - MAX_SESSION_DURATION) {
        delete this.recentReads[postId];
      }
    }
  }

  setCurrentPostRead(){
    let postId = getFromPdo('post.id');
    if (postId !== undefined) {
      this.recentReads[postId] = this.recentReads[postId] || { time: Date.now(), readsCount: 0, };
      this.recentReads[postId].readsCount++;
    }
  }

  isLoginExpired(){
    return !utilsCookies.get(LOGGED_IN_COOKIE_NAME) || this.authenticatedAt < (Date.now() - USER_AUTHENTICATION_TTL);
  }

  fbLoggingIn(){
    this.isAuthenticating = true;
    facebookAuthenticationProvider.login();
    this.emitChange();
  }

  existingUserAuthenticated(userData){
    this.isAuthenticating = false;
    this.isAuthenticated = true;
    this.authenticatedAt = Date.now();
    this.assignData(userData);
  }

  newUserAuthenticated(userData){
    this.draftUser = userData;
    // this.assignData(useraData);
  }

  authenticationFailed(response){
    this.warn('User Authentication Failed', response);
    this.isAuthenticating = false;
    this.removeFromStorage();
    this.emitChange();
  }

  assignData(data){
    Object.assign(this.state, data);
    this.emitChange();
  }

  emitChange(){
    this.saveToStorage();
    return super.emitChange();
  }

  saveToStorage(){
    // Note: for backwards compatibility, the key for this store in local storage should be 'CurrentUser'
    try {
      safeLocalStorage.set(STORAGE_KEY, this.state);
    } catch (ex) {
      this.warn('failed saving user data from localStorage', ex);
    }
    return this;
  }

  logoutUser(){
    this.state.isLoggedIn = false;
    this.isAuthenticated = false;
    this.isAuthenticating = false;
    this.removeFromStorage();
  }

  removeFromStorage(){
    safeLocalStorage.remove(STORAGE_KEY);
    window.location = LOGOUT_PATH;
  }
  
  loadFromStorage(){
    let userData = {};
    try {
      userData = safeLocalStorage.get(STORAGE_KEY, {});
    } catch (e) {
      this.warn('failed loading/parsing user data from localStorage', e);
    }
    this.assignData(userData);
    this.locale = utilsCookies.get('locale') || userData.locale || DEFAULT_LOCALE;
    return this;
  }

  getStartDate(){
    let date = safeLocalStorage.get(CLASS_CONSTATNTS.USER.START_DATE);
    if (!date) {
      date = new Date().toString();
      safeLocalStorage.set(CLASS_CONSTATNTS.USER.START_DATE, date);
    }
    return Date.parse(date);
  }

  get guestId(){
    let id = safeLocalStorage.get(GUEST_ID_KEY, utilsCookies.get(GUEST_ID_KEY));
    if (!id) {
      id = this.generateGuestUserId();
      utilsCookies.set(GUEST_ID_KEY, id);
      safeLocalStorage.set(GUEST_ID_KEY, id);
      this.guestId = id;
    }
    return id;
  }

  set guestId(guestId){ this.assignData({ guestId, }); }

  get partnerId(){ return this.state.partnerId; }

  generateGuestUserId(){
    return `${randId()}${randId()}-${randId()}-${randId()}-${randId()}-${randId()}${randId()}${randId()}`;
  }

  get isNewSession(){
    if (this.newSessionFlag === undefined) {
      let sessionStartTime = safeLocalStorage.get(CLASS_CONSTATNTS.USER.SESSION_START_TIME) || 0;
      this.newSessionFlag = sessionStartTime < Date.now() - MAX_SESSION_DURATION;
    }
    return this.newSessionFlag;
  }

  resetSessionStartTime(){
    safeLocalStorage.set(CLASS_CONSTATNTS.USER.SESSION_START_TIME, Date.now());
  }

}

const userStore = UserStore.create();
registerInNamespace({ userStore, });

export default userStore;



// WEBPACK FOOTER //
// ./provolution/app/views/provolution/jsCore/organizedByType/stores/userStore.js